import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const login = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    // Keep avatar cache in sync so it's always fresh
    if (userData.img && (userData.u_id || userData.id)) {
      localStorage.setItem(`avatar_${userData.u_id || userData.id}`, userData.img);
    }
    setUser(userData);
    setIsLoginModalOpen(false);
    // Notify CartContext / FavoritesContext so they load this user's data immediately (no refresh needed)
    window.dispatchEvent(new Event('storage'));
  };

  const logout = (reason) => {
    // Preserve the profile image URL keyed by user id so it survives logout
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      if (u.img && (u.u_id || u.id)) {
        localStorage.setItem(`avatar_${u.u_id || u.id}`, u.img);
      }
    } catch (_) {}
    localStorage.removeItem('user');
    setUser(null);
    window.dispatchEvent(new Event('storage'));
    if (reason === 'inactive') {
      // Tell the support widget to open and show the "contact admin" message.
      window.dispatchEvent(new CustomEvent('account-disabled'));
    }
  };

  // Poll the backend for this user's active status. If an admin disables the
  // account mid-session, force a logout and surface the contact-admin prompt.
  useEffect(() => {
    if (!user) return undefined;
    const uid = user.u_id || user.user_id || user.id;
    if (!uid) return undefined;

    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`/api/users/status/${uid}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data && data.is_active === false) {
          logout('inactive');
        }
      } catch (_) {
        // Network hiccup — don't log the user out on a failed check.
      }
    };

    check();
    const interval = setInterval(check, 20000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user]);

  const openLoginModal = () => setIsLoginModalOpen(true);
  const closeLoginModal = () => setIsLoginModalOpen(false);

  return (
    <AuthContext.Provider value={{
      user,
      isLoggedIn: !!user,
      isLoginModalOpen,
      login,
      logout,
      openLoginModal,
      closeLoginModal
    }}>
      {children}
    </AuthContext.Provider>
  );
};
